<script setup lang="ts">
// biome-ignore lint/style/useImportType: the template renders <OverlayPanel>, so the runtime binding is required
import OverlayPanel from "primevue/overlaypanel";
import { computed, onBeforeUnmount, ref } from "vue";
import linkIcon from "@/images/icon-link-api-navy-blue.svg";

type Props = {
  url: string | null | undefined;
};

const props = withDefaults(defineProps<Props>(), {
  url: undefined,
});

const hasUrl = computed<boolean>(() => {
  const trimmed = props.url?.trim();
  return typeof trimmed === "string" && trimmed.length > 0;
});

const normalizedUrl = computed<string>(() => {
  const trimmed = props.url?.trim() ?? "";
  if (!trimmed) {
    return "";
  }
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
});

const op = ref<InstanceType<typeof OverlayPanel> | undefined>(undefined);
let hideTimer: ReturnType<typeof setTimeout> | undefined;

const cancelHide = (): void => {
  if (hideTimer !== undefined) {
    clearTimeout(hideTimer);
    hideTimer = undefined;
  }
};

const scheduleHide = (): void => {
  cancelHide();
  hideTimer = setTimeout(() => {
    op.value?.hide();
    hideTimer = undefined;
  }, 200);
};

const onTriggerMouseOver = (event: MouseEvent): void => {
  cancelHide();
  if (hasUrl.value) {
    op.value?.show(event);
  }
};

onBeforeUnmount(() => {
  cancelHide();
});
</script>

<template>
  <span class="flex justify-center">
    <span
      :class="hasUrl ? 'cursor-pointer' : 'opacity-40'"
      data-testid="url-icon"
      @mouseover.stop="onTriggerMouseOver"
      @mouseleave.stop="scheduleHide"
    >
      <img :src="linkIcon" alt="link icon" />
    </span>
    <OverlayPanel
      v-if="hasUrl"
      ref="op"
      :pt="{ content: { class: 'p-3 bg-white max-w-md break-all' } }"
    >
      <div @mouseover="cancelHide" @mouseleave="scheduleHide">
        <a
          :href="normalizedUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-teal-900 underline text-sm"
          data-testid="url-popover-link"
        >
          {{ url }}
        </a>
      </div>
    </OverlayPanel>
  </span>
</template>
